// Notated duration — the score model's rhythm truth.
//
// The score stores WHAT is written (a dotted quarter; an eighth inside a 3:2
// tuplet); time in ticks is derived. No stored tick resolution can be "not
// divisible enough" (the 48-ticks-per-beat chart melody cannot hold a
// quintuplet) because nothing is stored in ticks. Arithmetic is exact
// rational (`Fraction`), so nested tuplets never accumulate float drift;
// integer ticks appear only at the playback/interchange boundary.

export type NoteValue =
  | 'breve'
  | 'whole'
  | 'half'
  | 'quarter'
  | 'eighth'
  | '16th'
  | '32nd'
  | '64th'
  | '128th';

export interface Duration {
  base: NoteValue;
  /** Augmentation dots (each adds half the previous value). */
  dots?: 0 | 1 | 2 | 3;
}

/** Derived-tick resolution at the playback boundary: 960 per quarter. */
export const PPQ = 960;

// ---------------------------------------------------------------------------
// Exact rational arithmetic
// ---------------------------------------------------------------------------

export interface Fraction {
  num: number;
  den: number;
}

function gcd(a: number, b: number): number {
  return b === 0 ? Math.abs(a) : gcd(b, a % b);
}

/** Normalised fraction: lowest terms, positive denominator. */
export function frac(num: number, den = 1): Fraction {
  if (den === 0) throw new Error('Fraction with zero denominator');
  const sign = den < 0 ? -1 : 1;
  const g = gcd(num, den) || 1;
  return { num: (sign * num) / g, den: (sign * den) / g };
}

export function addF(a: Fraction, b: Fraction): Fraction {
  return frac(a.num * b.den + b.num * a.den, a.den * b.den);
}

export function mulF(a: Fraction, b: Fraction): Fraction {
  return frac(a.num * b.num, a.den * b.den);
}

export function equalsF(a: Fraction, b: Fraction): boolean {
  return a.num * b.den === b.num * a.den;
}

export function compareF(a: Fraction, b: Fraction): number {
  return a.num * b.den - b.num * a.den;
}

export function fractionToNumber(f: Fraction): number {
  return f.num / f.den;
}

/** Recover a compact exact fraction from a UI number. Score cursors cross the
 * React boundary as numbers, but imported quintuplet/septuplet onsets must
 * return to 1/5 or 1/7 rather than an arbitrary fixed tick grid. */
export function fractionFromNumber(value: number, maxDenominator = 1_000_000): Fraction {
  if (!Number.isFinite(value)) throw new Error(`Cannot convert ${value} to a fraction`);
  if (!Number.isInteger(maxDenominator) || maxDenominator < 1) {
    throw new Error(`Invalid maximum denominator: ${maxDenominator}`);
  }
  const sign = value < 0 ? -1 : 1;
  const target = Math.abs(value);
  if (target === 0) return frac(0);

  let x = target;
  let previousNumerator = 0;
  let numerator = 1;
  let previousDenominator = 1;
  let denominator = 0;
  while (true) {
    const whole = Math.floor(x);
    const nextNumerator = whole * numerator + previousNumerator;
    const nextDenominator = whole * denominator + previousDenominator;
    if (nextDenominator > maxDenominator) break;
    previousNumerator = numerator;
    numerator = nextNumerator;
    previousDenominator = denominator;
    denominator = nextDenominator;
    if (Math.abs(numerator / denominator - target) <= 1e-12) break;
    const remainder = x - whole;
    if (remainder <= Number.EPSILON) break;
    x = 1 / remainder;
  }
  return frac(sign * numerator, denominator || 1);
}

// ---------------------------------------------------------------------------
// Duration values
// ---------------------------------------------------------------------------

/** Length of each base value in quarter notes. */
const BASE_QUARTERS: Record<NoteValue, Fraction> = {
  breve: frac(8),
  whole: frac(4),
  half: frac(2),
  quarter: frac(1),
  eighth: frac(1, 2),
  '16th': frac(1, 4),
  '32nd': frac(1, 8),
  '64th': frac(1, 16),
  '128th': frac(1, 32),
};

export const NOTE_VALUES: readonly NoteValue[] = [
  'breve',
  'whole',
  'half',
  'quarter',
  'eighth',
  '16th',
  '32nd',
  '64th',
  '128th',
];

/**
 * Written length in quarter notes, before any tuplet scaling. Dots follow the
 * standard geometric sum: one dot ×3/2, two ×7/4, three ×15/8.
 */
export function durationQuarters(d: Duration): Fraction {
  const dots = d.dots ?? 0;
  return mulF(BASE_QUARTERS[d.base], frac(2 ** (dots + 1) - 1, 2 ** dots));
}

/**
 * Sounding length in quarters under a tuplet scale factor (the product of the
 * enclosing tuplets' normal/actual ratios; 1/1 outside any tuplet).
 */
export function soundingQuarters(d: Duration, tupletFactor: Fraction = frac(1)): Fraction {
  return mulF(durationQuarters(d), tupletFactor);
}

/** Derived integer ticks (rounded only at this boundary). */
export function durationTicks(d: Duration, tupletFactor: Fraction = frac(1)): number {
  const q = soundingQuarters(d, tupletFactor);
  return Math.round((q.num * PPQ) / q.den);
}

/** Length of a measure in quarter notes for a time signature. */
export function measureQuarters([beats, unit]: [number, number]): Fraction {
  return frac(beats * 4, unit);
}
